import type { SeasonRegion } from '@sea-snag/shared';
import type { QueryClient } from '@tanstack/react-query';
import { Store } from '@tanstack/store';

export interface OrganizationData {
  pid: string;
  name: string;
  slug: string;
  seasonRegion?: SeasonRegion;
  setupCompletedAt: string | null;
  role?: string;
}

interface OrgStoreState {
  organization: OrganizationData | null;
  organizations: OrganizationData[];
}

const STORAGE_KEY = 'organization';

function loadFromStorage(): OrganizationData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as Record<string, unknown>;
    // Migrate old format: { id } → { pid }
    if (data['id'] && !data['pid']) {
      data['pid'] = data['id'];
      delete data['id'];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
    // Normalize missing setupCompletedAt from pre-onboarding localStorage shape
    if (!('setupCompletedAt' in data)) {
      data['setupCompletedAt'] = null;
    }
    return data as unknown as OrganizationData;
  } catch {
    return null;
  }
}

export function pickInitialOrg(orgs: OrganizationData[]): OrganizationData | null {
  if (orgs.length === 0) return null;

  const stored = loadFromStorage();
  if (stored && orgs.some((o) => o.pid === stored.pid)) {
    return stored;
  }

  return orgs[0]!;
}

export const orgStore = new Store<OrgStoreState>({
  organization: loadFromStorage(),
  organizations: [],
});

// ── localStorage persistence ──────────────────────────────────────────

let fromStorageEvent = false;

orgStore.subscribe(() => {
  if (fromStorageEvent) return;

  const { organization } = orgStore.state;
  if (organization) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(organization));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
});

// ── Multi-tab sync ────────────────────────────────────────────────────

window.addEventListener('storage', (e) => {
  if (e.key !== STORAGE_KEY) return;

  fromStorageEvent = true;
  if (e.newValue) {
    try {
      const org = JSON.parse(e.newValue) as OrganizationData;
      orgStore.setState((prev) => ({ ...prev, organization: org }));
    } catch {
      // ignore parse errors
    }
  } else {
    orgStore.setState((prev) => ({ ...prev, organization: null }));
  }
  fromStorageEvent = false;
});

// ── Exported actions ──────────────────────────────────────────────────

export function setOrganization(org: OrganizationData | null): void {
  orgStore.setState((prev) => ({ ...prev, organization: org }));
}

export function setOrganizations(orgs: OrganizationData[]): void {
  orgStore.setState((prev) => ({ ...prev, organizations: orgs }));
}

export function switchOrganization(org: OrganizationData, queryClient: QueryClient): void {
  setOrganization(org);
  void queryClient.invalidateQueries();
}

export function clearOrganization(): void {
  orgStore.setState((prev) => ({ ...prev, organization: null }));
}

export function getOrganization(): OrganizationData | null {
  return orgStore.state.organization;
}

// ── Super Admin: recent companies ────────────────────────────────────

const RECENT_ADMIN_COMPANIES_KEY = 'recentAdminCompanies';
const MAX_RECENT_COMPANIES = 5;

export interface RecentAdminCompany {
  pid: string;
  name: string;
  slug: string;
}

export function getRecentAdminCompanies(): RecentAdminCompany[] {
  try {
    const raw = localStorage.getItem(RECENT_ADMIN_COMPANIES_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as RecentAdminCompany[];
  } catch {
    return [];
  }
}

export function addRecentAdminCompany(company: RecentAdminCompany): void {
  const existing = getRecentAdminCompanies();
  const filtered = existing.filter((c) => c.pid !== company.pid);
  const updated = [company, ...filtered].slice(0, MAX_RECENT_COMPANIES);
  localStorage.setItem(RECENT_ADMIN_COMPANIES_KEY, JSON.stringify(updated));
}

export function removeRecentAdminCompany(pid: string): void {
  const updated = getRecentAdminCompanies().filter((c) => c.pid !== pid);
  localStorage.setItem(RECENT_ADMIN_COMPANIES_KEY, JSON.stringify(updated));
}
